import { array } from '@ember/helper';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import joinClasses from 'webapp/helpers/join-classes';
import type { TrackedRequestState } from 'webapp/components/metric-details/forecasts/explorer';

import type { DataSelect } from 'webapp/models-custom';
import Forecast from 'webapp/models-custom/forecast';
import RegressorMetric from 'webapp/models-custom/regressor-metric';

interface Args {
	model: Forecast | RegressorMetric;
	mainForecastRequestState: TrackedRequestState;
}

export default class TableRowForecastCellComponent extends Component<Args> {
	get isForecastRow(): boolean {
		return this.args.model instanceof Forecast;
	}

	get isRegressorRow(): boolean {
		return this.args.model instanceof RegressorMetric;
	}

	get showLoading(): boolean {
		if (this.isForecastRow) {
			return this.args.mainForecastRequestState?.isLoading ?? false;
		} else {
			return (this.args.model as RegressorMetric).isLoading;
		}
	}

	get showError(): boolean {
		if (this.isForecastRow) {
			return !!this.args.mainForecastRequestState?.error;
		} else {
			const regressor = this.args.model as RegressorMetric;
			return !!regressor.error || regressor.hasMetricUnavailableError;
		}
	}

	get isDisabled(): boolean {
		if (this.args.model.hasNoMetricPermission) {
			return true;
		}

		if (this.isRegressorRow) {
			const regressor = this.args.model as RegressorMetric;
			return !regressor.enabled || regressor.hasMetricUnavailableError;
		}

		return false;
	}

	get showDataValues(): boolean {
		return !!this.historicalValue || !!this.forecastValue;
	}

	get showCompareValue(): boolean {
		return this.isForecastRow;
	}

	@cached
	get lastHistoricalPoint() {
		if (this.isForecastRow) {
			const dataSelect = (this.args.model as Forecast).currentValueDataSelect;
			return this.getLastPointFromDataSelect(dataSelect);
		} else if (this.isRegressorRow) {
			const dataSelect = (this.args.model as RegressorMetric).currentValueDataSelect;
			return this.getLastPointFromDataSelect(dataSelect);
		}
		return;
	}

	@cached
	get lastForecastPoint() {
		return this.getLastPointFromDataSelect(this.args.model.forecastWholeRangeDataSelect);
	}

	get historicalValue(): string | undefined {
		if (this.args.model.hasNoMetricPermission) {
			return '-';
		}
		return this.lastHistoricalPoint?.valueStr;
	}

	get forecastValue(): string | undefined {
		if (this.args.model.hasNoMetricPermission) {
			return '-';
		}
		return this.lastForecastPoint?.valueStr;
	}

	private getLastPointFromDataSelect(dataSelect: DataSelect | null) {
		if (!dataSelect) {
			return;
		}

		const output = dataSelect?.outputs?.[0];
		return output?.points?.filter((p) => p.hasValue)?.sort((a, b) => b.ts - a.ts)[0];
	}
	<template>
		<div
			class={{joinClasses
				(array
					"summary-column-content"
					(if this.showError "error-cell")
					(if this.isForecastRow "forecast-cell")
					(if this.isDisabled "disabled-cell")
				)
			}}
		>
			{{#if this.showLoading}}
				<div class="animated-gradient skeleton-items">
					<div class="line line-50"></div>
					<div class="line line-25"></div>
				</div>
			{{else if this.showDataValues}}
				<div class="summary-cell-values">
					{{#if this.historicalValue}}
						<span class="data-value historical">{{this.historicalValue}}</span>
					{{else}}
						<span class="data-value historical empty">-</span>
					{{/if}}
					{{#if this.forecastValue}}
						<span class="data-value forecast">{{this.forecastValue}}</span>
					{{else}}
						<span class="data-value forecast empty">-</span>
					{{/if}}
					{{#if this.showCompareValue}}
						{{! Empty span for vertical alignment with compare row in table-row-data }}
						<span class="data-value compare empty"></span>
					{{/if}}
				</div>
			{{else}}
				{{! Fallback - shouldn't happen with updated logic }}
				<span class="data-value forecast empty">-</span>
			{{/if}}
		</div>
	</template>
}
